/**
 * *"Open this case in iTax / GST / TDS."* FR-E15, T-6.10. **[gated: spec §9-7]**
 *
 * **This component renders nothing in every environment today**, and that is
 * the correct behaviour rather than a gap. `core-app-integration`'s hand-off
 * broker is P1 and unbuilt, so no firm's `/internal/state` answers `active` for
 * `ca-erp-itax`, so `buttons()` is empty, so there is no button. FR-E15 says
 * *"the button appears only when the org has enabled the integration and the
 * platform reports `active: true`. No stub."*
 *
 * Three things it deliberately does not do:
 *
 * **It does not decide whether a button should exist.** `available` is the
 * server's answer, folding the live integration read and whether the platform
 * published anywhere to go. A second copy of that rule in TypeScript would
 * agree today and disagree the first time the contract moves, and the one the
 * firm sees would be the wrong one.
 *
 * **It does not render an error when there is nothing to show.** A firm that
 * has not bought iTax is not looking at a broken screen; the absence is the
 * whole message. The Integrations screen is where a firm is told what it does
 * not have and why (`routes/Firm/Integrations.tsx`).
 *
 * **It does not hide the one thing the firm can fix.** A client with no PAN
 * gets the button, disabled, with the sentence — ADR-0016 §5: a hand-off
 * *"requires a PAN and says so when it is missing"*. Every other reason belongs
 * to the platform and there is nothing here for anybody to do about it.
 *
 * The link is an ordinary anchor to core's own launch target wearing
 * `buttonClass` — which that helper exists for: *"an `onClick` that assigns
 * `location.href` takes middle-click, open-in-new-tab and the status bar away
 * from a link that should have had them"*. It opens in a new tab with
 * `noopener`, because the destination is another product on another origin and
 * the firm's case screen should still be there when they come back.
 */

import { Alert, Button, buttonClass } from '@fiveox/ui'
import { ExternalLink } from 'lucide-react'
import { useEffect, useState } from 'react'

import { type CaseHandoff, blockedMessage, buttons, handoff } from '../../lib/api/integration'

export function HandoffButtons({ caseId }: { caseId: string }) {
  const [state, setState] = useState<CaseHandoff | null>(null)

  useEffect(() => {
    let live = true
    // No error state on purpose: this read failing means the same as it
    // answering "nothing is enabled" — no button. Putting a warning on a case
    // screen for an integration nobody in this firm has heard of would be
    // noise on the busiest screen in the product.
    handoff
      .forCase(caseId)
      .then((res) => live && setState(res.data))
      .catch(() => live && setState(null))
    return () => {
      live = false
    }
  }, [caseId])

  const available = state ? buttons(state.targets) : []
  if (available.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {available.map((target) => {
          const blocked = blockedMessage(target)
          return blocked ? (
            <Button key={target.code} variant="outline" size="sm" disabled>
              <ExternalLink size={14} aria-hidden /> Open in {target.label}
            </Button>
          ) : (
            <a
              key={target.code}
              href={target.launch_url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              title={target.description}
              className={buttonClass({
                variant: 'outline',
                size: 'sm',
                className: 'inline-flex items-center gap-1',
              })}
            >
              <ExternalLink size={14} aria-hidden /> Open in {target.label}
            </a>
          )
        })}
      </div>
      {available
        .filter((target) => target.blocked)
        .slice(0, 1)
        .map((target) => (
          <Alert key={target.code} tone="warning">
            {blockedMessage(target)}
          </Alert>
        ))}
    </div>
  )
}
